export const isTypeOfString = (value: unknown): value is string => typeof value === 'string';

export const nonEmpty = (s: string | null | undefined): boolean => !!s && s.trim().length > 0;

export const capitalizeFirst = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Replace multiple consecutive whitespaces with one whitespace
export const normalizeWhitespace = (value: string): string => value.trim().replace(/\s+/g, ' ');

// Replace null / undefined with empty string
export const normalize = (value: string | null | undefined): string => (value ?? '').trim();

export const normalizeOptionalString = (value: string | null | undefined): string | null => normalize(value) || null;

export const normalizeEmail = (email: string | null | undefined): string => normalize(email).toLowerCase();

export function formatDuration(durationInSeconds: number): string {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_MINUTE = 60 * MILLISECONDS_PER_SECOND;
const MILLISECONDS_PER_HOUR = 60 * MILLISECONDS_PER_MINUTE;
const MILLISECONDS_PER_DAY = 24 * MILLISECONDS_PER_HOUR;

export function formatDurationMs(durationInMs: number): string {
  let remainingMs = Math.max(0, Math.round(durationInMs));

  const days = Math.floor(remainingMs / MILLISECONDS_PER_DAY);
  remainingMs %= MILLISECONDS_PER_DAY;
  const hours = Math.floor(remainingMs / MILLISECONDS_PER_HOUR);
  remainingMs %= MILLISECONDS_PER_HOUR;
  const minutes = Math.floor(remainingMs / MILLISECONDS_PER_MINUTE);
  remainingMs %= MILLISECONDS_PER_MINUTE;
  const seconds = Math.floor(remainingMs / MILLISECONDS_PER_SECOND);
  const milliseconds = remainingMs % MILLISECONDS_PER_SECOND;
  const parts: string[] = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`, `${milliseconds}ms`);

  return parts.join(' ');
}

/**
 * Human list: "a", "a und b", "a, b und c" — the conjunction is passed in so the caller supplies the
 * translated word.
 */
export const joinWithConjunction = (parts: readonly string[], conjunction: string): string => {
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} ${conjunction} ${parts[parts.length - 1]}`;
};

export const matches = (str: string, search: string): boolean => {
  return str.toLowerCase().includes(search.trim().toLowerCase());
};

/**
 * Shared search tokenizer used by BOTH the client-side {@link matchesAllSearchWords} and the server-side
 * `buildContainsSearchWhere` (backend `util/prisma.search.util`), so word-splitting stays identical across the stacks.
 */
export const splitSearchWords = (search?: string | null): string[] =>
  (search ?? '').trim().split(/\s+/).filter(Boolean);

/**
 * Client-side free-text search predicate: true when ALL search words each match (case-insensitive substring) ANY of
 * the given field values. This is the CLIENT half of the same search contract as the server-side
 * `buildContainsSearchWhere` (backend `util/prisma.search.util`); the two are pinned together by
 * `backend/test/unit/search/search.contract.test.ts`.
 */
export const matchesAllSearchWords = (fields: string[], search: string): boolean => {
  const words = splitSearchWords(search);
  return words.length === 0 || words.every((word) => fields.some((field) => matches(field, word)));
};
